import React from 'react';
import { Card } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Facebook, Instagram, Linkedin, MessageCircle } from 'lucide-react';

const PLATFORMS = {
  facebook: {
    name: 'Facebook',
    icon: Facebook,
    color: 'text-blue-600',
    metrics: ['Connection Requests Sent', 'DMs Sent', 'Messages Received', 'Responses Received', 'New Connections Made', 'Follow-ups Done']
  },
  instagram: {
    name: 'Instagram',
    icon: Instagram,
    color: 'text-pink-600',
    metrics: ['Follow Requests Sent', 'DMs Sent', 'Story Replies', 'Comment Responses', 'New Followers', 'Follow-ups Done']
  },
  linkedin: {
    name: 'LinkedIn',
    icon: Linkedin,
    color: 'text-blue-700',
    metrics: ['Connection Requests Sent', 'InMails Sent', 'Messages Received', 'Responses Received', 'New Connections Made', 'Follow-ups Done']
  },
  tiktok: {
    name: 'TikTok',
    icon: MessageCircle,
    color: 'text-gray-900',
    metrics: ['DMs Sent', 'Comment Replies', 'Messages Received', 'Responses Received', 'New Followers', 'Follow-ups Done']
  }
};

export const SocialMediaProspectingTracker = ({ socialProspecting, onSocialProspectingChange }) => {
  const handleMetricChange = (platform, metric, value) => {
    const newData = {
      ...socialProspecting,
      [platform]: {
        ...socialProspecting[platform],
        [metric]: parseInt(value) || 0
      }
    };
    onSocialProspectingChange(newData);
  };

  const getPlatformTotal = (platform) => {
    if (!socialProspecting[platform]) return 0;
    return Object.values(socialProspecting[platform]).reduce((sum, val) => sum + (parseInt(val) || 0), 0);
  };

  const getGrandTotal = () => {
    return Object.keys(PLATFORMS).reduce((sum, platform) => sum + getPlatformTotal(platform), 0);
  };

  const getMetricKey = (metric) => {
    return metric.toLowerCase().replace(/\s+/g, '_');
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-[#E31837]">Social Media Prospecting Tracker</h2>
        <Badge className="bg-[#E31837] text-lg px-4 py-1">
          Total Activities: {getGrandTotal()}
        </Badge>
      </div>
      
      <Tabs defaultValue="facebook" className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-4">
          {Object.entries(PLATFORMS).map(([key, platform]) => {
            const Icon = platform.icon;
            const total = getPlatformTotal(key);
            return (
              <TabsTrigger key={key} value={key} className="flex items-center gap-2">
                <Icon className={`h-4 w-4 ${platform.color}`} />
                <span className="hidden sm:inline">{platform.name}</span>
                {total > 0 && (
                  <Badge className="ml-1 bg-[#E31837]">{total}</Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.entries(PLATFORMS).map(([platformKey, platform]) => {
          const Icon = platform.icon;
          return (
            <TabsContent key={platformKey} value={platformKey} className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border">
                <Icon className={`h-6 w-6 ${platform.color}`} />
                <div>
                  <h3 className="font-bold text-[#333333]">{platform.name} Daily Prospecting</h3>
                  <p className="text-sm text-gray-600">Track all your prospecting activities on {platform.name}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {platform.metrics.map((metric) => {
                  const metricKey = getMetricKey(metric);
                  const value = socialProspecting[platformKey]?.[metricKey] || 0;
                  
                  return (
                    <div key={metric} className="space-y-2">
                      <Label htmlFor={`${platformKey}-${metricKey}`} className="text-sm font-medium flex items-center justify-between">
                        <span>{metric}</span>
                        {value > 0 && (
                          <Badge variant="outline" className="text-xs">{value}</Badge>
                        )}
                      </Label>
                      <Input
                        id={`${platformKey}-${metricKey}`}
                        type="number"
                        min="0"
                        value={value}
                        onChange={(e) => handleMetricChange(platformKey, metricKey, e.target.value)}
                        placeholder="0"
                        data-testid={`social-${platformKey}-${metricKey}`}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Platform Summary */}
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-[#333333]">Total {platform.name} Activities Today:</span>
                  <span className="text-2xl font-bold text-[#E31837]">{getPlatformTotal(platformKey)}</span>
                </div>
              </div>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Overall Summary */}
      <div className="mt-6 p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-lg border-2 border-[#E31837]">
        <h3 className="font-bold text-lg text-[#333333] mb-3">Today's Social Media Prospecting Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(PLATFORMS).map(([key, platform]) => {
            const Icon = platform.icon;
            const total = getPlatformTotal(key);
            return (
              <div key={key} className="text-center p-3 bg-white rounded-lg">
                <Icon className={`h-6 w-6 mx-auto mb-2 ${platform.color}`} />
                <p className="text-xs text-gray-600 mb-1">{platform.name}</p>
                <p className="text-xl font-bold text-[#E31837]">{total}</p>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
};
